import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { SERVICES, CLINIC2 } from "../data/content";
import { useSiteContent } from "../context/SiteContentContext";
import { MapPin, Phone, Clock, ShieldCheck, HeartHandshake, Stethoscope, Video, ArrowRight, ExternalLink } from "lucide-react";

export default function Home() {
  const navigate = useNavigate();
  const { hero, clinic, services: svcContent } = useSiteContent();
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <span className="inline-block bg-[#D9E4E1] text-slate-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-5">
              {hero.badge}
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-tight">
              {hero.title}
            </h1>
            <p className="mt-5 text-slate-600 text-base md:text-lg leading-relaxed max-w-xl">
              {hero.intro}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button
                data-testid="hero-book-appointment-btn"
                onClick={() => navigate("/booking")}
                className="rounded-full bg-[#5E8B7E] hover:bg-[#4A7065] text-white px-8 py-6 text-base"
              >
                Book an Appointment <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                data-testid="hero-online-consult-btn"
                variant="outline"
                onClick={() => navigate("/online-consultation")}
                className="rounded-full border-[#2C557E] text-[#2C557E] hover:bg-[#2C557E]/5 px-8 py-6 text-base"
              >
                <Video className="w-4 h-4 mr-2" /> Online Consultation · ₹100
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500">
              <span className="flex items-center gap-2"><Stethoscope className="w-4 h-4 text-[#5E8B7E]" />{clinic.credentials}</span>
              <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#5E8B7E]" />{clinic.experience}</span>
            </div>
          </div>
          <div className="relative fade-up" style={{ animationDelay: "0.15s" }}>
            <img
              src="/images/dr-prathusha.jpg"
              alt="Dr. Prathusha Kolachana — Endogynecologist"
              data-testid="hero-doctor-photo"
              className="rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] w-full object-cover aspect-[4/3] object-top"
            />
            <div className="absolute -bottom-6 left-6 bg-white rounded-2xl shadow-lg px-5 py-4 flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-[#E8DAB2] flex items-center justify-center">
                <HeartHandshake className="w-5 h-5 text-slate-700" />
              </span>
              <div>
                <div className="font-heading font-semibold text-slate-800 text-sm">Patient-first philosophy</div>
                <div className="text-xs text-slate-500">Your rights & safety, always</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-white border-y border-[#EDF1EF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: ShieldCheck, title: "Private & Secure", text: "Your medical records are encrypted and visible only to you and your doctor — fully aligned with India's DPDP Act, 2023." },
            { icon: HeartHandshake, title: "Empathy First", text: "Sensitive concerns deserve unhurried conversations. Every consultation begins with listening, not prescribing." },
            { icon: Video, title: "Care, Anywhere", text: "Can't visit Banjara Hills? Book a 15-minute online consultation for just ₹100 — mornings and evenings, every day." },
          ].map((f) => (
            <div key={f.title} className="flex gap-4 p-4">
              <span className="w-11 h-11 rounded-xl bg-[#D9E4E1] flex items-center justify-center shrink-0">
                <f.icon className="w-5 h-5 text-[#4A7065]" />
              </span>
              <div>
                <h3 className="font-heading font-semibold text-slate-800">{f.title}</h3>
                <p className="text-sm text-slate-500 mt-1 leading-relaxed">{f.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="font-heading text-3xl md:text-4xl text-slate-900">How we can care for you</h2>
            <p className="mt-3 text-slate-600">
              From preconception planning through pregnancy and advanced gynaecological treatments —
              five core areas, one promise: personalised care in Hyderabad.
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <Link
                key={s.id}
                to={`/services#${s.id}`}
                data-testid={`home-service-card-${s.id}`}
                className="group bg-white rounded-2xl border border-[#EDF1EF] overflow-hidden hover:-translate-y-1 hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)] transition-all duration-300"
              >
                <img src={s.image} alt={s.name} className="h-40 w-full object-cover" loading="lazy" />
                <div className="p-5">
                  <h3 className="font-heading font-semibold text-slate-800 group-hover:text-[#4A7065] transition-colors">
                    {s.name}
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">{svcContent[s.id]?.tagline || s.tagline}</p>
                  <span className="inline-flex items-center text-sm text-[#5E8B7E] font-medium mt-3">
                    Learn more <ArrowRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
            <div className="bg-[#2C557E] rounded-2xl p-6 flex flex-col justify-center text-white">
              <h3 className="font-heading font-semibold text-lg">Not sure where to start?</h3>
              <p className="text-sm text-blue-100 mt-2">
                Book a consultation and let's talk it through together — no concern is too small.
              </p>
              <Button
                data-testid="home-services-cta"
                onClick={() => navigate("/booking")}
                className="mt-4 rounded-full bg-[#E8DAB2] text-slate-800 hover:bg-[#dccd9e] w-fit"
              >
                Book a Visit
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* LOCATION STRIP — two consultation locations */}
      <section className="bg-[#F4F7F6] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <h2 className="font-heading text-2xl md:text-3xl text-slate-900">Visit the clinic</h2>
            <Button
              variant="outline"
              onClick={() => navigate("/contact")}
              className="rounded-full border-[#5E8B7E] text-[#5E8B7E] hover:bg-[#D9E4E1]/50 w-fit"
              data-testid="home-contact-btn"
            >
              Directions & Contact
            </Button>
          </div>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                id: "care",
                name: "Care Hospitals",
                address: clinic.address,
                hours: clinic.hours,
                embed: clinic.mapEmbed,
                link: clinic.mapsLink,
              },
              {
                id: "trust",
                name: CLINIC2.name,
                address: CLINIC2.address,
                hours: CLINIC2.hours,
                embed: CLINIC2.mapEmbed,
                link: CLINIC2.mapsLink,
              },
            ].map((loc) => (
              <div
                key={loc.id}
                data-testid={`location-card-${loc.id}`}
                className="bg-white rounded-2xl border border-[#EDF1EF] overflow-hidden"
              >
                <iframe
                  title={`${loc.name} location map`}
                  src={loc.embed}
                  className="w-full h-52 border-0"
                  loading="lazy"
                />
                <div className="p-5">
                  <h3 className="font-heading font-semibold text-slate-800">{loc.name}</h3>
                  <div className="mt-3 space-y-2 text-sm text-slate-600">
                    <p className="flex gap-2"><MapPin className="w-4 h-4 text-[#5E8B7E] shrink-0 mt-0.5" />{loc.address}</p>
                    <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#5E8B7E]" /><a href={clinic.phoneHref} className="hover:text-[#4A7065]">{clinic.phone}</a></p>
                    <p className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#5E8B7E]" />{loc.hours}</p>
                  </div>
                  <a href={loc.link} target="_blank" rel="noreferrer" data-testid={`open-maps-${loc.id}`}>
                    <Button size="sm" variant="outline" className="mt-4 rounded-full border-[#5E8B7E] text-[#5E8B7E] hover:bg-[#D9E4E1]/50">
                      Open in Maps <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                    </Button>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
